import React, { useEffect, useMemo, useState } from "react";
import {
  Backdrop, CircularProgress, Tooltip, Typography, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Stack, TextField, Switch,
  FormControl, FormLabel, IconButton,
  Box
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DataTable from "comps/Tables/DataTable";
import TLinkApi from "services/tlink.api";
import MDButton from "components/MDButton";
import { Autocomplete } from "@mui/material";
import MDInput from "components/MDInput";
import MRTClientDataTable from "comps/Tables/DataTable/MRTClientDataTable";
import { MRT_ShowHideColumnsButton, MRT_ToggleFiltersButton, MRT_ToggleFullScreenButton } from "material-react-table";

const BASE_ENDPOINT = "/configurations/supplier-blocked-config";

const SupplierBlockedConfig = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [data, setData] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState("add");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [supplierId, setSupplierId] = useState("");
  const [languageId, setLanguageId] = useState("");
  const [id, setId] = useState("");
  const [isBlocked, setIsBlocked] = useState(false);

  const [originalSupplierId, setOriginalSupplierId] = useState("");
  const [originalLanguageId, setOriginalLanguageId] = useState("");

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await TLinkApi.get(BASE_ENDPOINT);
      const rows = (res?.data ?? []).map((r) => ({
        id: r.id,
        supplier_id: r.supplier_id,
        supplier_name: r.supplier_name,
        language_id: r.language_id,
        language_name: r.language_name,
        is_blocked: !!r.is_blocked,
        created_at: r.created_at,
        updated_at: r.updated_at,
      }));
      setData(rows);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const [languages, setLanguage] = useState([]);
  // const [languageMap, setLanguageMap] = useState([]);

  const getLanguage = async () => {
    try {
      const res = await TLinkApi.get("/masters/language");
      const list = (res?.data ?? [])
        .map((v) => ({ id: Number(v.id), name: String(v.name) }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setLanguage([{ id: -1, name: "All Languages" }, ...list]);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    getLanguage();
  }, []);

  const keysSet = useMemo(() => {
    const s = new Set();
    for (const r of data) s.add(`${r.supplier_id}:${r.language_id}`);
    return s;
  }, [data]);

  const openAdd = () => {
    setMode("add");
    setSupplierId("");
    setLanguageId("");
    setId("");
    setIsBlocked(false);
    setOriginalSupplierId("");
    setOriginalLanguageId("");
    setErrorMsg("");
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setMode("edit");
    setSupplierId(String(row.supplier_id ?? ""));
    setLanguageId(String(row.language_id ?? ""));
    setId(String(row.id ?? ""));
    setIsBlocked(Boolean(row.is_blocked));
    setOriginalSupplierId(String(row.supplier_id ?? ""));
    setOriginalLanguageId(String(row.language_id ?? ""));
    setErrorMsg("");
    setModalOpen(true);
  };

  const valid = supplierId.trim() !== "" && languageId.trim() !== "";

  const handleSave = async () => {
    if (!valid) {
      setErrorMsg("Supplier ID and Language ID are required.");
      return;
    }
    const newKey = `${Number(supplierId)}:${Number(languageId)}`;
    const oldKey = `${Number(originalSupplierId)}:${Number(originalLanguageId)}`;

    if (mode === "add" && keysSet.has(newKey)) {
      setErrorMsg("This supplier/language pair already exists.");
      return;
    }
    if (mode === "edit" && newKey !== oldKey && keysSet.has(newKey)) {
      setErrorMsg("Another record already uses this supplier/language pair.");
      return;
    }

    setSubmitting(true);
    setErrorMsg("");
    try {
      const payload = {
        supplierId: Number(supplierId),
        languageId: Number(languageId),
        isBlocked: Boolean(isBlocked),
      };

      if (mode === "add") {
        await TLinkApi.post(BASE_ENDPOINT, payload);
      } else {
        payload.previousSupplierId = Number(originalSupplierId);
        payload.previousLanguageId = Number(originalLanguageId);
        await TLinkApi.update(`${BASE_ENDPOINT}/${id}`, payload);
      }

      setModalOpen(false);
      await fetchData();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Something went wrong";
      setErrorMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };
  const oneLine = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

  const columns = useMemo(
    () => [
      {
        accessor: "supplier_id",
        Header: "Supplier Id",
        align: "left",
        Cell: ({ row }) => {
          const v = row?.original?.supplier_id;
          return <div>{(v === null || v === undefined || v === "") ? "-" : v}</div>;
        },
      },
      {
        accessor: "supplier_name",
        Header: "Supplier Name",
        align: "left",
        sortable: true,
        Cell: ({ row }) => {
          const name = row?.original?.supplier_name || "";
          const display = name ? (name.length > 20 ? name.slice(0, 20) + "..." : name) : "-";
          return (
            <Tooltip title={name && name.length > 20 ? name : ""}>
              <div style={oneLine}>{display}</div>
            </Tooltip>
          );
        },
      },
      // {
      //   accessor: "language_name",
      //   Header: "Language",
      //   align: "left",
      //   Cell: ({ row }) => {
      //     const id = Number(row.original.language_id);
      //     const name = id === -1 ? "All Languages" : (row.original.language_name || "-");
      //     return (
      //       <Tooltip title={name}>
      //         <div>{name}</div>
      //       </Tooltip>
      //     );
      //   },
      // },
      {
        accessor: "language_name",
        Header: "Language",
        align: "left",
        Cell: ({ row }) => {
          const id = Number(row?.original?.language_id);
          const raw = row?.original?.language_name ?? "";
          const name = id === -1 ? "All Languages" : (raw || "-");
          const showTip = id !== -1 && !!raw && raw.length > 20;
          return (
            <Tooltip title={showTip ? raw : ""}>
              <div>{name}</div>
            </Tooltip>
          );
        },
      },
      {
        accessor: "is_blocked",
        Header: "Blocked",
        align: "center",
        headerAlign: "center",
        Cell: ({ row }) => (row.original.is_blocked ? "Yes" : "No"),
      },
      {
        accessor: "actions",
        Header: "Actions",
        align: "right",
        headerAlign: "right",
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end" pr={1}>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => openEdit(row.original)} sx={{ p: 0.5 }}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    []
  );

  const [fullScr, setFullScr] = useState(false);
  const renderToolbarInternalActions = ({ table }) => {
    return (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: "0rem" }}>
          <Button variant="contained" color="info" onClick={openAdd}>
            + New
          </Button>
          <MRT_ShowHideColumnsButton table={table} />
          <MRT_ToggleFiltersButton table={table} />
          <div onClick={() => setFullScr(!fullScr)} >
            <MRT_ToggleFullScreenButton table={table} />
          </div>
        </div>
      </>
    );
  };

  return (
    <div>
      {isLoading && (
        <Backdrop sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }} open>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      {/* <DataTable
        table={{ columns, rows: data }}
        isSorted
        entriesPerPage={entriesPerPage}
        canSearch={false}
        tablePage={page}
        setTablePage={setPage}
        setEntriesPerPage={setEntriesPerPage}
        totalLength={data.length}
        pagination
        enableTopToolbar
        tableMaxHeight={data.length === 0 ? "calc(100vh - 220px)" : "calc(100vh - 220px)"}
        tableMinHeight={data.length === 0 ? "calc(100vh - 220px)" : "calc(100vh - 220px)"}
        renderTopToolbarCustomActions={() => (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: window.innerWidth > 1600 ? "calc(76vw - 580px)" : "calc(76vw - 565px)", marginTop: "3px" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Supplier Blocked Configuration
            </Typography>
            <Button variant="contained" color="error" onClick={openAdd} sx={{ ml: 2 }}>
              + New
            </Button>
          </Box>
        )}
      /> */}

      <MRTClientDataTable
        table={{ columns, rows: data }}
        isSorted
        canSearch={false}
        enableTopToolbar
        defaultPageSize={entriesPerPage}
        entriesOption={["10", "20", "30", "40", "50"]}
        tableMaxHeight="calc(100vh - 220px)"
        tableMinHeight="calc(100vh - 220px)"
        renderToolbarInternalActions={renderToolbarInternalActions}
        fullScr={fullScr}
        renderTopToolbarCustomActions={() => (
          <Box
            // sx={{
            //   display: "flex",
            //   justifyContent: "space-between",
            //   alignItems: "center",
            //   width:
            //     window.innerWidth > 1600
            //       ? "calc(76vw - 580px)"
            //       : "calc(76vw - 565px)",
            //   mt: "3px",
            // }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Supplier Blocked Configuration
            </Typography>
            {/* <Button variant="contained" color="error" onClick={openAdd} sx={{ ml: 2 }}>
              + New
            </Button> */}
          </Box>
        )}
      />

      <Dialog open={modalOpen} onClose={() => !submitting && setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{mode === "add" ? "Add" : "Update"}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Supplier ID</FormLabel>
              <TextField
                size="small"
                type="number"
                placeholder="Enter supplier ID"
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
              />
            </FormControl>

            <FormControl fullWidth>
              <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Language</FormLabel>
              <Autocomplete
                options={languages}
                value={languageId === "" ? null : (languages.find(l => l.id === Number(languageId)) || null)}
                onChange={(_, option) => {
                  setLanguageId(option ? String(option.id) : "");
                }}
                getOptionLabel={(opt) => opt?.name ?? ""}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <MDInput {...params} placeholder='Select language (or "All Languages")' size="small" />
                )}
                disableClearable={false}
              />
            </FormControl>

            <FormControl>
              <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Blocked</FormLabel>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch checked={isBlocked} onChange={(e) => setIsBlocked(e.target.checked)} />
                <Typography variant="body2">{isBlocked ? "Yes" : "No"}</Typography>
              </Stack>
            </FormControl>

            {errorMsg && (
              <Typography variant="body2" color="error">
                {errorMsg}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setModalOpen(false)}
            disabled={submitting}
          >
            Cancel
          </MDButton>

          <MDButton
            variant="contained"
            color="info"
            onClick={handleSave}
            disabled={submitting || !valid}
          >
            {submitting ? (mode === "add" ? "Saving…" : "Updating…") : (mode === "add" ? "Save" : "Update")}
          </MDButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SupplierBlockedConfig;
